import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { HOTEL } from "@/data/hotel";

const links = [
  { to: "/", label: "Home" },
  {
    label: "Our Hotels",
    children: [
      { to: "/properties", label: "All Properties" },
      { to: "/rooms", label: "Rooms" },
      { to: "/gallery", label: "Gallery" },
    ],
  },
  { to: "/careers", label: "Careers" },
  { to: "/partner", label: "Partner With Us" },
  { to: "/contact", label: "Contact" },
];

const NAV_ICON_SIZE = 22;

const slug = (s) => s.toLowerCase().replace(/[\s&]+/g, "-");

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setHoveredMenu(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-paper-50 border-b hairline shadow-sm"
          : "bg-paper-50/95 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-28 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img
            src={HOTEL.logo}
            alt={`${HOTEL.brand} — boutique hotels in Delhi`}
            className="h-[84px] w-auto drop-shadow-sm"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) =>
            l.children ? (
              <div
                key={l.label}
                className="relative"
                onMouseEnter={() => setHoveredMenu(l.label)}
                onMouseLeave={() => setHoveredMenu(null)}
              >
                <button
                  type="button"
                  data-testid={`nav-link-${slug(l.label)}`}
                  className="flex items-center gap-1 text-[13px] font-semibold tracking-[0.18em] uppercase text-ink-900 hover:text-sage-dark transition-colors"
                >
                  {l.label}
                  <ChevronDown size={14} className="opacity-70" />
                </button>
                {hoveredMenu === l.label && (
                  <div className="absolute left-0 top-full pt-3 z-50">
                    <div className="bg-paper-50 border hairline shadow-lg py-2 min-w-[220px]">
                      {l.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-sublink-${slug(c.label)}`}
                          className="block px-5 py-3 text-[13px] font-semibold tracking-[0.16em] uppercase text-ink-900 hover:text-sage-dark hover:bg-paper-100 transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${slug(l.label)}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold tracking-[0.18em] uppercase transition-colors ${
                    isActive ? "text-sage-dark" : "text-ink-900 hover:text-sage-dark"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/book"
            data-testid="nav-book-btn"
            className="hidden md:inline-flex items-center h-12 px-7 bg-sage text-paper-50 text-[13px] font-semibold tracking-[0.2em] uppercase hover:bg-sage-dark transition-colors"
          >
            Book Now
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-ink-900"
          >
            {open ? <X size={NAV_ICON_SIZE} /> : <Menu size={NAV_ICON_SIZE} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-paper-50 border-t hairline">
          <div className="px-6 py-8 flex flex-col gap-5">
            {links.map((l) =>
              l.children ? (
                <div key={l.label} className="flex flex-col gap-3">
                  <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-ink-500">
                    {l.label}
                  </div>
                  {l.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      data-testid={`mobile-nav-sublink-${slug(c.label)}`}
                      className="pl-3 text-base font-semibold tracking-[0.14em] uppercase text-ink-900"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${slug(l.label)}`}
                  className={({ isActive }) =>
                    `text-base font-semibold tracking-[0.14em] uppercase ${
                      isActive ? "text-sage-dark" : "text-ink-900"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
            <Link
              to="/book"
              data-testid="mobile-nav-book-btn"
              className="mt-2 inline-flex items-center justify-center h-12 bg-sage text-paper-50 text-sm font-semibold tracking-[0.18em] uppercase"
            >
              Book Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
